import { Component } from "react";

import { WorkspaceResourceType } from "common/interfaces";
import getObjectHash from "common/utils/getObjectHash";
import ThemesPaneController from "js/controllers/ThemesPaneController";
import WorkspaceController from "js/controllers/WorkspaceController";
import getLogger, { LogGroup } from "js/core/logger";
import themeManager, { SHARED_THEME_SORT } from "js/core/themeManager";

const logger = getLogger(LogGroup.THEMES);

/**
 * ThemesPaneController is a singleton whose initialize() resets it, so a second loader mounting
 * while a first is live would tear the first one's data down under it. Mounts are counted and the
 * first owns the fetch — three surfaces render a loader and two can be on screen at once. The
 * count is released back to zero on the last unmount, since other panes drive the same controller
 * with different filters.
 */
let activeLoaders = 0;
let initPromise = null;
let initWorkspaceId = null;
// Bumped on every initialize. Loaders release against the epoch they acquired in, so one from an
// earlier visit cannot decrement a count it never joined.
let initEpoch = 0;

/**
 * The fetch is stored to be shared, not awaited — every loader reads the controller's state instead.
 * A rejection therefore has nobody to catch it, so it is reported here rather than left unhandled.
 */
function initializeThemeList(options) {
    return Promise.resolve(ThemesPaneController.initialize(options))
        .catch(err => {
            logger.warn("[ThemeListLoader] could not load the workspace's themes", { message: err?.message });
        });
}

export function acquireThemeList(workspaceId) {
    if (activeLoaders === 0 || initWorkspaceId !== workspaceId) {
        // A workspace change starts a fresh count: loaders still mounted against the old one hold data
        // this re-initialize is about to replace.
        if (initWorkspaceId !== workspaceId) {
            activeLoaders = 0;
        }

        initEpoch++;
        initWorkspaceId = workspaceId;
        initPromise = initializeThemeList({
            resourceTypes: [WorkspaceResourceType.THEME],
            workspaceId,
            sort: [...SHARED_THEME_SORT],
            filters: [{
                field: "resource.metadata.isPublished",
                operator: "eq",
                value: true
            }]
        });
    }

    activeLoaders++;
    return { epoch: initEpoch, promise: initPromise };
}

export function releaseThemeList(epoch) {
    // A superseded epoch is not part of the current count; decrementing for it would release the
    // fetch while the loaders that do hold it are still mounted.
    if (epoch !== initEpoch) {
        return;
    }

    activeLoaders = Math.max(0, activeLoaders - 1);
    if (activeLoaders === 0) {
        initPromise = null;
        initWorkspaceId = null;
    }
}

/** Exported for its tests, which need the module-level counters back at a known state. */
export function __resetThemeListRefCountForTests() {
    activeLoaders = 0;
    initPromise = null;
    initWorkspaceId = null;
    initEpoch = 0;
}

/**
 * Loads the workspace's published themes, split into shared and the user's own. A render prop
 * because the theme picker's grid and the brand kit strip need this list and nothing else in common.
 */
export const ThemeListLoader = ThemesPaneController.withState(class ThemeListLoader extends Component {
    constructor() {
        super();

        this.state = {
            userThemes: [],
            sharedThemes: [],
            resources: [],
        };

        this.addThemePromises = {};

        this.workspaceId_ = WorkspaceController.workspaceId;
    }

    componentDidMount() {
        this.isMounted_ = true;
        // Remembered rather than re-derived on unmount: the release has to match the exact count this
        // mount joined, and both the workspace and the epoch can move on.
        this.acquiredEpoch = acquireThemeList(this.workspaceId_).epoch;

        // Read straight away, not only on change: a loader joining an already-initialized controller gets
        // no transition to react to.
        this.syncThemes();
    }

    componentWillUnmount() {
        this.isMounted_ = false;

        if (this.acquiredEpoch !== undefined) {
            const epoch = this.acquiredEpoch;
            this.acquiredEpoch = undefined;
            releaseThemeList(epoch);
        }
    }

    get isCurrentWorkspace() {
        return this.props.workspaceId === this.workspaceId_;
    }

    componentDidUpdate(prevProps) {
        const { data, initialized } = this.props;

        if (!initialized || !this.isCurrentWorkspace) {
            return;
        }

        // Load more themes until we have them all, this component should be refactored to do proper pagination
        if ((prevProps.initialized !== initialized || prevProps.data.length !== data.length) && ThemesPaneController.canLoadMore) {
            ThemesPaneController.loadMore();
        }

        if (prevProps.initialized !== initialized || getObjectHash(data) !== getObjectHash(prevProps.data)) {
            this.syncThemes();
        }
    }

    /** Loads every theme in the controller's current page that this loader hasn't loaded yet. */
    syncThemes() {
        const { data, initialized } = this.props;

        if (!initialized || !this.isCurrentWorkspace) {
            return;
        }

        for (const { resource } of data ?? []) {
            if (resource.metadata.isShared) {
                this.addSharedTheme(resource);
            } else {
                this.addUserTheme(resource);
            }
        }
    }

    async addSharedTheme(resource) {
        const id = resource.resourceId;
        if (!this.addThemePromises[id]) {
            this.addThemePromises[id] = (async () => {
                const theme = await themeManager.getSharedTheme(id, true);
                if (!this.isMounted_) {
                    return;
                }
                this.setState(state => ({
                    ...state,
                    sharedThemes: [...state.sharedThemes, theme],
                    resources: [...state.resources, resource],
                }));
            })();
        }

        return this.addThemePromises[id];
    }

    async addUserTheme(resource) {
        const id = resource.resourceId;
        if (!this.addThemePromises[id]) {
            this.addThemePromises[id] = (async () => {
                const theme = await themeManager.getUserTheme(id, true);
                if (!this.isMounted_) {
                    return;
                }
                this.setState(state => ({
                    ...state,
                    userThemes: [...state.userThemes, theme],
                    resources: [...state.resources, resource],
                }));
            })();
        }

        return this.addThemePromises[id];
    }

    render() {
        const { children, initialized, fetching } = this.props;
        const { userThemes, sharedThemes, resources } = this.state;

        const isReady = initialized && this.isCurrentWorkspace;

        return children({ userThemes, sharedThemes, resources, initialized: isReady, fetching });
    }
});
